<template>
  <div class="radar-chart-container">
    <canvas ref="radarCanvas" v-show="!pdfImageReady"></canvas>
    <img v-if="pdfImageReady" :src="chartImageData" class="chart-image" />
  </div>
</template>

<script>
import Chartjs from "chart.js/auto";

export default {
  name: "RadarChart",
  props: {
    resultScore: {
      type: Array,
      required: true,
    },
    isPDF: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      chart: null,
      pdfImageReady: false,
      chartImageData: null,
    };
  },
  mounted() {
    this.setupChart();
  },
  watch: {
    resultScore: {
      handler() {
        this.setupChart();
      },
      deep: true,
    },
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
  methods: {
    setupChart() {
      if (!this.resultScore || this.resultScore.length === 0) return;

      // 如果是 PDF 模式且已經有圖片數據，直接返回
      if (this.isPDF && this.pdfImageReady) return;

      // 銷毀現有圖表
      if (this.chart) {
        this.chart.destroy();
      }

      // 依據 id 順序排列智能類型（順時針）
      const sortedData = [...this.resultScore].sort((a, b) => a.id - b.id);

      const labels = sortedData.map((item) => item.name);
      const data = sortedData.map((item) => item.total);

      const chartData = {
        labels: labels,
        datasets: [
          {
            label: "智能分數",
            data: data,
            backgroundColor: "#FBEBD5",
            borderWidth: 0,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
        ],
      };

      const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        scales: {
          r: {
            beginAtZero: true,
            min: 0,
            max: 40,
            ticks: {
              stepSize: 10,
              callback: function (value) {
                return value;
              },
              font: {
                family: "GenSenRounded, Noto Sans TC, sans-serif",
                size: this.isPDF ? 28 : window.innerWidth >= 768 ? 20 : 8,
                weight: 300,
              },
              color: "#838383",
              backdropColor: "transparent",
              z: 10,
            },
            grid: {
              color: "#F5B0B280",
              lineWidth: this.isPDF ? 2 : 1,
              borderDash: window.innerWidth >= 768 ? [8, 5] : [4, 2],
              borderDashOffset: 0,
              z: 10,
            },
            angleLines: {
              color: "#F5B0B280",
              lineWidth: this.isPDF ? 3 : 1.5,
              z: 10,
            },
            pointLabels: {
              font: {
                family: "GenSenRounded, Noto Sans TC, sans-serif",
                size: this.isPDF ? 28 : window.innerWidth >= 768 ? 20 : 8,
                weight: 700,
              },
              color: "#333",
            },
          },
        },
        animation: {
          duration: 0, // 禁用動畫以確保立即渲染完成
        },
      };

      // 創建新圖表
      const ctx = this.$refs.radarCanvas.getContext("2d");
      this.chart = new Chartjs(ctx, {
        type: "radar",
        data: chartData,
        options: chartOptions,
      });

      // 如果是 PDF 模式，在圖表渲染完成後準備圖片
      if (this.isPDF) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.prepareForPDF();
          }, 100);
        });
      }
    },

    // 準備 PDF 擷取的方法
    prepareForPDF() {
      return new Promise((resolve) => {
        if (!this.chart) {
          resolve();
          return;
        }

        try {
          // 獲取圖表的 base64 圖片數據
          this.chartImageData = this.chart.toBase64Image("image/png", 1);
          this.pdfImageReady = true;

          console.log("RadarChart PDF 圖片準備完成");
          resolve();
        } catch (error) {
          console.error("RadarChart PDF 準備失敗:", error);
          resolve();
        }
      });
    },

    // 重置 PDF 狀態
    resetPDFState() {
      this.pdfImageReady = false;
      this.chartImageData = null;
    },
  },
};
</script>

<style scoped>
.radar-chart-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.chart-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
